import { useMemo } from 'react'

import { RanksByGenreResult } from '../../../api/top-grossing'
import { ColorPercentage } from '../../visual-explorer/types'
import { VisualAnalysisWithRank, VisualsGroupedData } from '../types'
import { VisualAnalysis } from './../../../api/connect'
import { RankType } from './../types/index'

type UseIconGrouper = (
  rankType: RankType,
  top: number,
  genreId: string,
  attrData?: VisualAnalysis[],
  rankData?: RanksByGenreResult,
  colorPercentage?: ColorPercentage
) => VisualsGroupedData

const createUniqueByAppId = (rankType: RankType, top: number, genreId: string, attrData: VisualAnalysis[], rankData: RanksByGenreResult) => {
  const initial: VisualsGroupedData = { top: [], out: [] }
  if (!rankData || !attrData) {
    return initial
  }

  const conventionalFields = (d: VisualAnalysis) => ({
    conventionalCategory: d.conventionalCategory,
    conventionalCategoryId: d.conventionalCategoryId,
    conventionalGenre: d.conventionalGenre,
    conventionalGenreId: d.conventionalGenreId,
    conventionalSubgenre: d.conventionalSubgenre,
    conventionalSubgenreId: d.conventionalSubgenreId,
  })

  return attrData.reduce((acc, data) => {
    let item = acc[data.appId]
    if (item) {
      const attributes = { ...item.attributes, ...data.attributes }
      const colorGroups = { ...item.colorGroups, ...data.colorGroups }
      const colors = { ...item.colors, ...data.colors }
      const fromGenre = data.model === 'genre' ? conventionalFields(data) : {}
      item = { ...item, attributes, colorGroups, colors, ...fromGenre }
    } else {
      const sdrank: number = rankData.sdranks[data.appId]
      const srank: number = rankData.sranks[data.appId]
      item = { ...data, genreId, rankType, top, sdrank, srank }
    }
    return { ...acc, [data.appId]: item }
  }, {} as { [appId: number]: VisualAnalysisWithRank })
}

export const useIconGrouper: UseIconGrouper = (rankType, top, genreId, attrData, rankData, colorPercentage) => {
  return useMemo(() => {
    const initial: VisualsGroupedData = { top: [], out: [] }
    if (!rankData || !attrData) {
      return initial
    }

    const uniqueByAppId = createUniqueByAppId(rankType, top, genreId, attrData, rankData)
    return Object.values(uniqueByAppId).reduce((acc, analysis) => {
      const rank = rankType === 'free' ? analysis.sdrank : analysis.srank
      const topOrOut = rank <= top ? 'top' : 'out'
      let include = true
      if (colorPercentage) {
        Object.keys(colorPercentage).forEach((color) => {
          const foundColor = (analysis.colorGroups && analysis.colorGroups[color]) || (analysis.colors && analysis.colors[color])
          // Normalize to 0-100 scale: API may return 0-1 (fraction) or 0-100 (percent)
          const valueAsPercent = foundColor == null ? 0 : foundColor <= 1 ? foundColor * 100 : foundColor
          const requiredPercent = colorPercentage[color]
          if (valueAsPercent < requiredPercent) {
            include = false
          }
        })
      }
      return include ? { ...acc, [topOrOut]: [...acc[topOrOut], analysis] } : acc
    }, initial)
  }, [attrData, rankData, top, genreId, rankType, colorPercentage])
}

type UseMapVisualAnalysisWithRank = (
  rankType: RankType,
  top: number,
  genreId: string,
  attrData?: VisualAnalysis[],
  rankData?: RanksByGenreResult,
  colorPercentage?: ColorPercentage
) => VisualAnalysisWithRank[]
export const useMapVisualAnalysisWithRank: UseMapVisualAnalysisWithRank = (rankType, top, genreId, attrData, rankData, colorPercentage) => {
  return useMemo(() => {
    const initial: VisualAnalysisWithRank[] = []
    if (!rankData || !attrData) {
      return initial
    }

    const uniqueByAppId = createUniqueByAppId(rankType, top, genreId, attrData, rankData)
    const items = Object.values(uniqueByAppId)
    if (!colorPercentage || Object.keys(colorPercentage).length === 0) {
      return items
    }

    // Apply the same per-color threshold filter as useIconGrouper so that
    // changing the per-color amount on the parent page propagates to views
    // that consume this list (e.g. Game Name Stats tab).
    return items.filter((analysis) => {
      return Object.keys(colorPercentage).every((color) => {
        const foundColor = (analysis.colorGroups && analysis.colorGroups[color]) || (analysis.colors && analysis.colors[color])
        const valueAsPercent = foundColor == null ? 0 : foundColor <= 1 ? foundColor * 100 : foundColor
        return valueAsPercent >= colorPercentage[color]
      })
    })
  }, [attrData, rankData, top, genreId, rankType, colorPercentage])
}
